import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { formatDateDDMMYYYY, formatNaira } from "@/lib/format";
import { FileSignature, CheckCircle2 } from "lucide-react";

export const PortalContracts = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [signing, setSigning] = useState(false);
  const [sig, setSig] = useState({ full_name: "", signature_text: "", accept: false });

  const load = useCallback(async () => {
    const r = await api.get("/contracts");
    setContracts(r.data);
    if (id) {
      const found = r.data.find(c => c.id === id);
      if (found) setSelected(found);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (user && !sig.full_name) setSig(s => ({ ...s, full_name: user.name, signature_text: `/s/ ${user.name}` }));
  }, [user, sig.full_name]);

  const sign = async () => {
    if (!sig.accept) return toast.error("Please tick the box to accept the contract");
    try {
      const r = await api.post(`/contracts/${selected.id}/sign`, sig);
      toast.success("Contract signed", { description: "Sirod Secur has been notified" });
      setSelected(r.data);
      setSigning(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const status = (s) => {
    const c = { PAID: "#2D9E5F", DRAFT: "#8CA0B8", SENT: "#C9A84C", VIEWED: "#C9A84C", OVERDUE: "#E63946", SIGNED: "#2D9E5F" };
    return c[s] || "#8CA0B8";
  };

  return (
    <div className="space-y-4">
      <div className="s-card-light" style={{ padding: 0 }}>
        <table className="s-table s-table-light">
          <thead><tr><th>Contract</th><th>Monthly fee</th><th>Term</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {contracts.map(c => (
              <tr key={c.id} onClick={() => setSelected(c)} style={{ cursor: "pointer" }} data-testid={`pc-${c.id}`}>
                <td>{c.title}</td>
                <td className="mono">{formatNaira(c.monthly_fee_naira)}</td>
                <td className="mono" style={{ fontSize: 12 }}>
                  {formatDateDDMMYYYY(c.start_date)} → {formatDateDDMMYYYY(c.end_date)}
                </td>
                <td>
                  <span style={{ padding: "4px 10px", borderRadius: 9999, fontSize: 12,
                                 color: status(c.status), background: status(c.status) + "20" }}>
                    {c.status}
                  </span>
                </td>
                <td>
                  {c.status !== "SIGNED" ? (
                    <button onClick={(e) => { e.stopPropagation(); setSelected(c); setSigning(true); }}
                            className="btn-primary inline-flex items-center gap-2"
                            style={{ padding: "6px 12px", fontSize: 12 }} data-testid={`sign-${c.id}`}>
                      <FileSignature size={12} /> Review & sign
                    </button>
                  ) : (
                    <span className="text-success inline-flex items-center gap-1" style={{ fontSize: 12 }}>
                      <CheckCircle2 size={12} /> Signed
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {contracts.length === 0 && <tr><td colSpan={5} className="text-muted text-center" style={{ padding: 24 }}>No contracts yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && (
        <div onClick={() => { setSelected(null); setSigning(false); }} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 100,
        }}>
          <div onClick={(e) => e.stopPropagation()} className="s-card-light"
               style={{ width: 720, maxWidth: "95vw", maxHeight: "92vh", overflowY: "auto", background: "#fff" }}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="h-section" style={{ color: "#0D1B2A" }}>{selected.title}</div>
                <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
                  {formatDateDDMMYYYY(selected.start_date)} → {formatDateDDMMYYYY(selected.end_date)} · {formatNaira(selected.monthly_fee_naira)} / month
                </div>
              </div>
              <button onClick={() => { setSelected(null); setSigning(false); }} className="text-muted" style={{ fontSize: 18 }}>✕</button>
            </div>

            <pre className="mono" style={{
              fontSize: 12, whiteSpace: "pre-wrap", color: "#0D1B2A",
              background: "#F4F6F9", padding: 16, borderRadius: 8, lineHeight: 1.6,
              border: "1px solid var(--border-light)",
            }}>{selected.body}</pre>

            {renderSignSection(selected, signing, sig, setSig, setSigning, sign)}
          </div>
        </div>
      )}
    </div>
  );
};

const renderSignSection = (selected, signing, sig, setSig, setSigning, sign) => {
  if (selected.signature) {
    return (
      <div className="mt-4 p-4 rounded-lg" style={{ background: "#F4F6F9", border: "1px solid var(--border-light)" }}>
        <div className="text-muted" style={{ fontSize: 12 }}>SIGNED ELECTRONICALLY</div>
        <div style={{ fontSize: 15, color: "#0D1B2A", marginTop: 4 }}>{selected.signature.full_name}</div>
        <div className="mono" style={{ fontSize: 20, color: "#2ECAC8", marginTop: 6 }}>{selected.signature.signature_text}</div>
        <div className="text-muted mono" style={{ fontSize: 11, marginTop: 6 }}>{selected.signed_at}</div>
      </div>
    );
  }
  if (signing) {
    return (
      <div className="mt-4 p-4 rounded-lg" style={{ background: "#F4F6F9", border: "1px solid var(--border-light)" }}>
        <div className="h-section mb-2" style={{ color: "#0D1B2A", fontSize: 15 }}>Sign this contract</div>
        <label className="text-muted" style={{ fontSize: 12 }}>Full legal name</label>
        <input value={sig.full_name} onChange={(e) => setSig({...sig, full_name: e.target.value})}
               className="s-input light mt-1 mb-3" data-testid="sig-name" />
        <label className="text-muted" style={{ fontSize: 12 }}>Typed signature</label>
        <input value={sig.signature_text} onChange={(e) => setSig({...sig, signature_text: e.target.value})}
               className="s-input light mt-1 mb-3 mono" style={{ fontSize: 18 }} data-testid="sig-text" />
        <label className="flex items-center gap-2 mb-4" style={{ fontSize: 13, color: "#0D1B2A" }}>
          <input type="checkbox" checked={sig.accept} onChange={(e) => setSig({...sig, accept: e.target.checked})} data-testid="sig-accept" />
          I have read and agree to the terms above.
        </label>
        <div className="flex gap-2">
          <button onClick={() => setSigning(false)} className="btn-outline flex-1">Cancel</button>
          <button onClick={sign} className="btn-primary flex-1" data-testid="sig-submit">Sign electronically</button>
        </div>
      </div>
    );
  }
  return (
    <button onClick={() => setSigning(true)} className="btn-primary mt-4 w-full inline-flex items-center justify-center gap-2"
            data-testid="start-sign">
      <FileSignature size={14} /> Review & sign
    </button>
  );
};
